// src/pages/CallToolsDashboard.js - Admin dashboard for CallTools analyses
import React, { useEffect, useState } from "react";
import { getToken } from "../auth";
import { useNavigate, Link } from "react-router-dom";
import {
  Phone, BarChart2, Clock, Calendar, User, Download, Eye, FileText,
  RefreshCw, Settings, ArrowLeft, CheckCircle, XCircle, AlertCircle, Trash2
} from "lucide-react";

const API_URL = "https://call-analyzer-backend.onrender.com";

const CallToolsDashboard = () => {
  const [analyses, setAnalyses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhooks, setWebhooks] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate("/login");
        return;
      }

      // Fetch analyses and stats in parallel
      const [analysesRes, statsRes, webhooksRes] = await Promise.all([
        fetch(`${API_URL}/calltools/analyses?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/calltools/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/calltools/webhooks`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (analysesRes.ok) {
        const data = await analysesRes.json();
        setAnalyses(data.analyses || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (webhooksRes.ok) {
        const data = await webhooksRes.json();
        setWebhooks(data.results || []);
      }

    } catch (err) {
      setError("Error loading data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const syncTranscripts = async () => {
    setSyncing(true);
    setError("");
    setSuccess("");

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/calltools/sync?limit=20`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Synced ${data.analyzed_count} new analyses, skipped ${data.skipped_count}`);
        fetchData(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.detail || "Sync failed");
      }
    } catch (err) {
      setError("Sync error: " + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const setupWebhook = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("webhook_url", webhookUrl);

      const response = await fetch(`${API_URL}/calltools/setup-webhook`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess("Webhook registered successfully!");
        setWebhookUrl("");
        fetchData();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to register webhook");
      }
    } catch (err) {
      setError("Error: " + err.message);
    }
  };

  const viewAnalysis = async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/calltools/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedAnalysis(data);
      }
    } catch (err) {
      setError("Error loading analysis");
    }
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm("Are you sure you want to delete this analysis?")) {
      return;
    }
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/calltools/analysis/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess("Analysis deleted successfully");
        setAnalyses(analyses.filter(a => a.id !== id));
        fetchData(); // Refresh stats
      } else {
        setError("Failed to delete analysis");
      }
    } catch (err) {
      setError("Error deleting: " + err.message);
    }
  };

  const downloadReport = async (analysis) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/calltools/analysis/${analysis.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `call_analysis_${analysis.calltools_call_id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setError("Failed to generate PDF");
      }
    } catch (err) {
      setError("Error downloading PDF: " + err.message);
    }
  };

  // Format analysis text with proper styling
  const formatAnalysisText = (text) => {
    if (!text) return null;

    return text.split('\n').map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={index} className="h-2" />;

      // Headers
      if (trimmed.startsWith('###')) {
        return <h4 key={index} className="text-md font-semibold text-gray-800 mt-4 mb-2">{trimmed.replace(/^###\s*/, '')}</h4>;
      }
      if (trimmed.startsWith('##')) {
        return <h3 key={index} className="text-lg font-semibold text-emerald-700 mt-4 mb-2">{trimmed.replace(/^##\s*/, '')}</h3>;
      }
      if (trimmed.startsWith('#')) {
        return <h2 key={index} className="text-xl font-bold text-emerald-800 mt-4 mb-2">{trimmed.replace(/^#\s*/, '')}</h2>;
      }

      // Bold text
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return <p key={index} className="font-semibold text-gray-800 mt-2">{trimmed.replace(/\*\*/g, '')}</p>;
      }

      // Bullet points
      if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
        return <li key={index} className="ml-4 text-gray-700">{trimmed.substring(1).trim()}</li>;
      }

      // Score line
      if (trimmed.toLowerCase().includes('overall score:')) {
        return <p key={index} className="text-lg font-bold text-emerald-600 mt-3 p-2 bg-emerald-50 rounded">{trimmed}</p>;
      }

      // Regular paragraph
      return <p key={index} className="text-gray-700 mb-1">{trimmed}</p>;
    });
  };

  // Format transcript - break into readable sentences
  const formatTranscript = (text) => {
    if (!text) return null;

    // If text already has line breaks, use them
    if (text.includes('\n')) {
      const lines = text.split('\n').filter(line => line.trim());
      return (
        <div className="space-y-3">
          {lines.map((line, index) => (
            <p key={index} className="text-gray-700 leading-relaxed bg-gray-50 p-3 rounded border-l-4 border-emerald-400">
              {line.trim()}
            </p>
          ))}
        </div>
      );
    }

    // No line breaks - split into sentences for readability
    // Split on sentence endings followed by space and capital letter
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];

    // Group sentences into paragraphs (3-4 sentences each)
    const paragraphs = [];
    let currentParagraph = [];

    sentences.forEach((sentence, i) => {
      currentParagraph.push(sentence.trim());
      // Start new paragraph every 3-4 sentences or at natural breaks
      if (currentParagraph.length >= 3 ||
          sentence.includes('Okay') ||
          sentence.includes('All right') ||
          sentence.includes('Thank you')) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    });

    // Don't forget remaining sentences
    if (currentParagraph.length > 0) {
      paragraphs.push(currentParagraph.join(' '));
    }

    return (
      <div className="space-y-4">
        {paragraphs.map((para, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg border-l-4 border-emerald-500">
            <p className="text-gray-700 leading-relaxed">{para}</p>
          </div>
        ))}
      </div>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 7) return "text-green-600 bg-green-100";
    if (score >= 4) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CallTools Dashboard</h1>
              <p className="text-gray-600">Automatic call analysis from CallTools</p>
            </div>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowSetup(!showSetup)}
              className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              <Settings className="h-5 w-5 mr-2" />
              Setup
            </button>
            <button
              onClick={syncTranscripts}
              disabled={syncing}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <XCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            {success}
          </div>
        )}

        {/* Setup Panel */}
        {showSetup && (
          <div className="mb-8 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Webhook Setup</h2>
            <p className="text-gray-600 mb-4">
              Register a webhook with CallTools to automatically receive and analyze new call transcripts.
            </p>

            <form onSubmit={setupWebhook} className="flex space-x-4">
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder={`${API_URL}/calltools/webhook`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                required
              />
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Register Webhook
              </button>
            </form>

            {webhooks.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-gray-700 mb-2">Active Webhooks:</h3>
                <ul className="space-y-2">
                  {webhooks.map((wh, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {wh.resthook_model}: {wh.url}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Total Analyses</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_analyses}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <BarChart2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Average Score</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.average_score}/10</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Avg Duration</p>
                  <p className="text-2xl font-bold text-gray-900">{formatDuration(stats.average_duration_seconds)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Score Distribution</p>
                  <div className="flex space-x-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded">
                      High: {stats.score_distribution?.high || 0}
                    </span>
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-600 rounded">
                      Med: {stats.score_distribution?.medium || 0}
                    </span>
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded">
                      Low: {stats.score_distribution?.low || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analyses Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Recent Analyses</h2>
          </div>

          {analyses.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Phone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No CallTools analyses yet</p>
              <p className="text-sm mt-2">Click "Sync Now" to fetch and analyze recent transcripts</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disposition</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyses.map((analysis) => (
                    <tr key={analysis.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{analysis.calltools_call_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {analysis.contact_phone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {analysis.call_datetime
                          ? new Date(analysis.call_datetime).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDuration(analysis.duration_seconds)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(analysis.overall_score)}`}>
                          {analysis.overall_score?.toFixed(1) || '-'}/10
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {analysis.call_disposition || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {analysis.created_at
                          ? new Date(analysis.created_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => viewAnalysis(analysis.id)}
                          className="text-emerald-600 hover:text-emerald-800 mr-3"
                          title="View Details"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => deleteAnalysis(analysis.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Analysis Detail Modal */}
        {selectedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Call Analysis #{selectedAnalysis.calltools_call_id}
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadReport(selectedAnalysis)}
                    className="flex items-center px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
                    title="Download PDF Report"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => setSelectedAnalysis(null)}
                    className="p-2 text-gray-600 hover:text-gray-900"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Call Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedAnalysis.contact_phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium">
                      {selectedAnalysis.call_datetime
                        ? new Date(selectedAnalysis.call_datetime).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-medium">{formatDuration(selectedAnalysis.duration_seconds)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Score</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(selectedAnalysis.overall_score)}`}>
                      {selectedAnalysis.overall_score?.toFixed(1) || '-'}/10
                    </span>
                  </div>
                </div>

                {/* Analysis */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <BarChart2 className="h-5 w-5 mr-2 text-emerald-600" />
                    Analysis Results
                  </h3>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-6 border border-gray-200">
                    {formatAnalysisText(selectedAnalysis.analysis_text) || <p className="text-gray-500">No analysis available</p>}
                  </div>
                </div>

                {/* Transcript */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-emerald-600" />
                    Call Transcript
                  </h3>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-6 border border-gray-200 max-h-[500px] overflow-y-auto">
                    {formatTranscript(selectedAnalysis.transcript_text) || <p className="text-gray-500">No transcript available</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallToolsDashboard;
